import { useState } from 'react'
import { Button, Icon } from '@nazka/nazka.mapframe.components';
import { useNavigate, useParams } from 'react-router-dom';
import { faArrowLeft, faShareNodes } from '@fortawesome/pro-light-svg-icons';

import MapMarkerLayer from '@App/components/MapMarkerLayer/MapMarkerLayer';
import SharePopup from '@App/components/SharePopup/SharePopup';
import ImageWithFallback from '@App/components/ImageWithFallback/ImageWithFallback';
import ItemNotFound from '@App/components/ItemNotFound/ItemNotFound';

import { getImageUrl } from '@services/utils.service';
import { useTranslation } from '@utils/i18n/i18n.consumers';
import { HAZARD_CATEGORIES } from '@App/components/AdaptationListComponent/adaptation-list-component.constants';
import { usePublishedAdaptationPlans } from '@services/query.service';

import './plan-detail.styl'

function PlanDetail() {
  const [showSharePopup, setShowSharePopup] = useState(false);

  const { data: adaptationPlans = [], isLoading } = usePublishedAdaptationPlans();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { id } = useParams();

  const adaptationPlan = adaptationPlans?.find(plan => plan.id === id);

  if (!isLoading && !adaptationPlan) {
    return <ItemNotFound goBack={() => navigate('/adaptation/plans')} />;
  }

  return (
    <div className="plan-detail">
      <div className="plan-detail__header">
        <ImageWithFallback
          className="plan-detail__img"
          src={getImageUrl(adaptationPlan)}
          fallbackSrc="/img/plan-placeholder.png"
          alt=""
        />
        <div className="plan-detail__header__content">
          <h3>{adaptationPlan?.translations?.en?.title}</h3>
          <span className="plan-detail__category">{t('Plan')}</span>
        </div>
      </div>

      <div className="plan-detail__location">
        <span className="plan-detail__location__title">
          {t('Address')}
          :
        </span>
        <span>
          {adaptationPlan?.address}
        </span>
      </div>
      <div className="plan-detail__hazard">
        <span className="plan-detail__hazard__title">
          {t('Hazard category')}
        </span>
        <div className="plan-detail__hazard__names-wrapper">
          {adaptationPlan?.hazardCategories.map((hazardCategory) => {
            const hazard = HAZARD_CATEGORIES.find(h => h.id === hazardCategory);
            return hazard ? (
              <span className="plan-detail__hazard__name" key={hazard.id}>
                <Icon icon={hazard.icon} size="24px" />
                {t(hazard.id)}
              </span>
            ) : null;
          })}
        </div>
      </div>
      <div className="plan-detail__description">
        <span className="plan-detail__description__title">{t('What are you trying to adapt in your community and why is it important?')}</span>
        <p>{adaptationPlan?.translations?.en?.description}</p>
      </div>
      <div className="plan-detail__action-plan">
        <span className="plan-detail__action-plan__title">{t('What is your action plan for adapting it? List the different steps of your plan and stakeholders that need to be involved.')}</span>
        {adaptationPlan?.translations?.en?.plan?.map((plan, index) => (
          <p key={index}>{plan}</p>
        ))}
      </div>
      <div className="plan-detail__challenges">
        <span className="plan-detail__challenges__title">{t('What are the challenges to execute the adaptation action plan? Can you come up with any solutions for these challenges?')}</span>
        <p>{adaptationPlan?.translations?.en?.challenges}</p>
      </div>
      <div className="plan-detail__responsible">
        <span className="plan-detail__responsible__title">{t('Who is responsible for this adaptation plan?')}</span>
        <p>{adaptationPlan?.responsible}</p>
      </div>
      <div className="plan-detail__cta">
        <Button
          icon={faArrowLeft}
          css={{
            borderRadius: '10px',
            flexDirection: 'row-reverse',
            '&:hover': {
              color: 'white',
              backgroundColor: '#72A276',
            },
          }}
          layout="outlined"
          onClick={() => navigate('/adaptation/plans')}
        >
          {t('Go back')}
        </Button>
        <Button
          icon={faShareNodes}
          css={{
            borderRadius: '10px',
            color: 'white',
            flexDirection: 'row-reverse',
            '&:hover': {
              color: 'white',
            },
          }}
          onClick={() => setShowSharePopup(`${window.location.origin}/adaptation/plans/${adaptationPlan?.id}`)}
        >
          {t('Share')}
        </Button>
      </div>
      <MapMarkerLayer markerData={[adaptationPlan]} setSelectedMarker={() => {}} selectedMarker={null} isSingle />
      {showSharePopup && (
        <SharePopup
          qrCodeText={showSharePopup}
          onClose={() => setShowSharePopup(false)}
        />
      )}
    </div>
  )
}

export default PlanDetail